import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Bot, Sparkles, Wrench, Cog, FileText as SummaryIcon, CheckCircle2 } from 'lucide-react';
import { MessageBubble } from '../atoms/MessageBubble';
import type { DisplayAttachment } from './MessageAttachments';
import { cn } from '../../../utils/cn';
import { useLanguage } from '../../../features/language/useLanguage';

interface DisplayMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  attachments?: DisplayAttachment[];
}

interface MessageThreadProps {
  messages: DisplayMessage[];
  isLoading: boolean;
  isEmpty: boolean;
}

type LoadingStageId = 'finding_tool' | 'processing_request' | 'summarizing_response' | 'preparing_answer';

interface LoadingStage {
  id: LoadingStageId;
  titleKey: string;
  subtitleKey: string;
  icon: React.ElementType;
  color: string;
  doneColor: string;
}

const STAGE_DURATION_MS: Record<LoadingStageId, number> = {
  finding_tool: 5000,
  processing_request: 8000,
  summarizing_response: 8000,
  preparing_answer: 3000,
};

const FAST_STAGE_MS = 600;

type LoadingPhase = 'idle' | 'running' | 'waiting';

const SUGGESTION_KEYS = [
  'aiAssistant.messages.suggestions.s1',
  'aiAssistant.messages.suggestions.s2',
  'aiAssistant.messages.suggestions.s3',
  'aiAssistant.messages.suggestions.s4',
  'aiAssistant.messages.suggestions.s5',
  'aiAssistant.messages.suggestions.s6',
  'aiAssistant.messages.suggestions.s7',
  'aiAssistant.messages.suggestions.s8',
  'aiAssistant.messages.suggestions.s9',
  'aiAssistant.messages.suggestions.s10',
  'aiAssistant.messages.suggestions.s11',
  'aiAssistant.messages.suggestions.s12',
  'aiAssistant.messages.suggestions.s13',
  'aiAssistant.messages.suggestions.s14',
  'aiAssistant.messages.suggestions.s15',
  'aiAssistant.messages.suggestions.s16',
  'aiAssistant.messages.suggestions.s17',
  'aiAssistant.messages.suggestions.s18',
  'aiAssistant.messages.suggestions.s19',
  'aiAssistant.messages.suggestions.s20',
] as const;

/**
 * Returns the i18n key for a time-based greeting.
 * Pagi: 04:00–11:59, Siang: 12:00–14:59, Sore: 15:00–17:59, Malam: 18:00–03:59
 */
const getGreetingKey = (): string => {
  const hour = new Date().getHours();
  if (hour >= 4 && hour < 12) return 'aiAssistant.messages.greetings.morning';
  if (hour >= 12 && hour < 15) return 'aiAssistant.messages.greetings.afternoon';
  if (hour >= 15 && hour < 18) return 'aiAssistant.messages.greetings.evening';
  return 'aiAssistant.messages.greetings.night';
};

export const MessageThread: React.FC<MessageThreadProps> = ({ messages, isLoading, isEmpty }) => {
  const { t } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Loading flow state — stages must fully complete before revealing AI response.
  // If response arrives early, remaining stages run at FAST_STAGE_MS (still visible, but quick).
  const [phase, setPhase] = useState<LoadingPhase>('idle');
  const [activeStageIndex, setActiveStageIndex] = useState(0);
  const [frozenMessages, setFrozenMessages] = useState<DisplayMessage[] | null>(null);

  // Dynamic greeting & suggestion — resolved once per mount
  const greetingKey = useMemo(() => getGreetingKey(), []);
  const [suggestionKey] = useState(
    () => SUGGESTION_KEYS[Math.floor(Math.random() * SUGGESTION_KEYS.length)],
  );

  const allStages = useMemo<LoadingStage[]>(() => [
  {
    id: 'finding_tool',
    titleKey: 'aiAssistant.loading.findingTool.title',
    subtitleKey: 'aiAssistant.loading.findingTool.subtitle',
    icon: Wrench,
    color: 'bg-blue-400',
    doneColor: 'bg-[#0277BD]',
  },
  {
    id: 'processing_request',
    titleKey: 'aiAssistant.loading.processingRequest.title',
    subtitleKey: 'aiAssistant.loading.processingRequest.subtitle',
    icon: Cog,
    color: 'bg-red-400',
    doneColor: 'bg-[#E11E35]',
  },
  {
    id: 'summarizing_response',
    titleKey: 'aiAssistant.loading.summarizingResponse.title',
    subtitleKey: 'aiAssistant.loading.summarizingResponse.subtitle',
    icon: SummaryIcon,
    color: 'bg-green-400',
    doneColor: 'bg-[#1B5E20]',
  },
  {
    id: 'preparing_answer',
    titleKey: 'aiAssistant.loading.preparingAnswer.title',
    subtitleKey: 'aiAssistant.loading.preparingAnswer.subtitle',
    icon: CheckCircle2,
    color: 'bg-amber-400',
    doneColor: 'bg-[#FF8F00]',
  },
], []);

  // Start stage sequence when loading begins — freeze current messages so the new
  // AI response stays hidden until all stages have played.
  if (isLoading && phase === 'idle') {
    setPhase('running');
    setActiveStageIndex(0);
    setFrozenMessages(messages);
  }

  // Advance stages. If isLoading already false (response arrived early), remaining
  // stages run at FAST_STAGE_MS so the user isn't stuck watching filler.
  useEffect(() => {
    if (phase !== 'running') return;
    const stage = allStages[activeStageIndex];
    if (!stage) return;

    const duration = isLoading ? STAGE_DURATION_MS[stage.id] : FAST_STAGE_MS;
    const timer = setTimeout(() => {
      const nextIndex = activeStageIndex + 1;
      if (nextIndex >= allStages.length) {
        setPhase('waiting');
      } else {
        setActiveStageIndex(nextIndex);
      }
    }, duration);

    return () => clearTimeout(timer);
  }, [phase, activeStageIndex, allStages, isLoading]);

  // Stages completed; reveal the real messages once the response has also arrived.
  if (phase === 'waiting' && !isLoading) {
    setPhase('idle');
    setFrozenMessages(null);
    setActiveStageIndex(0);
  }

  const showLoader = phase !== 'idle';
  const displayedMessages = phase === 'idle' ? messages : (frozenMessages ?? messages);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setTimeout(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, 50); // slight delay to allow rendering
  }, [displayedMessages, showLoader, activeStageIndex]);

  if (isEmpty && !isLoading && !showLoader) {
    return (
      <div className="flex-1 flex items-center justify-center" data-test-id="message-thread-empty">
        <div className="text-center space-y-4 max-w-3xl px-4">
          <style>{`
            @keyframes rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            .animate-rotate { animation: rotate 2s linear infinite; }
            @keyframes shine { 0% { background-position: 200% center; } 100% { background-position: -200% center; } }
            .animate-shine { background-size: 200% auto; animation: shine 4s linear infinite; }
          `}</style>

          <div className="flex items-center justify-center space-x-3">
            <div className="relative p-[2px] rounded-lg overflow-hidden flex items-center justify-center">
              <div
                className="absolute inset-[-100%] animate-rotate"
                style={{ background: 'conic-gradient(from 0deg, transparent 0%, #818cf8 25%, transparent 50%)' }}
              />
              <div className="relative z-10 p-1.5 bg-slate-900 rounded-[7px] flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-indigo-400" />
              </div>
            </div>
            <span className="text-gray-400 font-medium tracking-wide">
              {t(greetingKey)} {t('aiAssistant.messages.hiThere')}
            </span>
          </div>

          <h2
            className="text-3xl md:text-5xl font-bold bg-clip-text text-transparent animate-shine py-2 leading-normal"
            style={{
              backgroundImage: 'linear-gradient(to right, #ffffff 20%, #a5b4fc 40%, #a5b4fc 60%, #ffffff 80%)',
              backgroundSize: '200% auto',
            }}
          >
            {t(suggestionKey)}
          </h2>

          <p className="text-xs text-gray-600">
            {t('aiAssistant.messages.savedAutomatically')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto min-h-0 space-y-5 pr-2 custom-scrollbar"
      data-test-id="message-thread"
    >
      {displayedMessages.map(msg => (
        <MessageBubble key={msg.id} role={msg.role} content={msg.content} attachments={msg.attachments} />
      ))}

      {/* Loading indicator */}
      {showLoader && (() => {
        const currentStage = allStages[activeStageIndex];
        return (
          <div className="flex gap-4 animate-in fade-in" data-test-id="message-thread-loading">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center shrink-0">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div className="bg-gray-800/40 border border-white/5 rounded-2xl px-5 py-4 w-full max-w-[85%] transition-all duration-300">
              <div className="flex items-center gap-2">
                <currentStage.icon className="h-4 w-4 text-indigo-300 animate-pulse" />
                <span className="text-sm font-medium text-indigo-100">
                  {t(currentStage.titleKey)}
                </span>
              </div>
              <p className="text-xs text-gray-300 mt-1 transition-opacity duration-200">
                {t(currentStage.subtitleKey)}
              </p>
              
              <div className="mt-3 grid grid-cols-4 gap-1.5">
                {allStages.map((stage, idx) => {
                  const isActive = activeStageIndex === idx;
                  const isDone = activeStageIndex > idx;
                  return (
                    <div
                      key={stage.id}
                      className={cn(
                        'h-1.5 rounded-full transition-all duration-300',
                        isActive && `${stage.color} animate-pulse`,
                        isDone && stage.doneColor,
                        !isActive && !isDone && 'bg-gray-600/60'
                      )}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
